<template>
    <ContentField>
        <div class="chat-log-page">
            <div class="header-row">
                <div>
                    <h3>对话日志查询</h3>
                    <div class="sub-title">查询服务器记录的提问内容和大模型返回内容，时间按东八区显示。</div>
                </div>
                <button class="btn btn-primary" @click="loadLogs(1)">查询</button>
            </div>

            <div class="recording-panel">
                <h5>recording 账号回放设置</h5>
                <div class="row g-3 align-items-end">
                    <div class="col-md-4">
                        <label class="form-label">唯一 recording 账号</label>
                        <input v-model="recordingConfig.recordingUsername" type="text" class="form-control" placeholder="例如 arlabtest">
                    </div>
                    <div class="col-md-4">
                        <label class="form-label">唯一调用账号</label>
                        <input v-model="recordingConfig.targetUsername" type="text" class="form-control" placeholder="例如 cs">
                    </div>
                    <div class="col-md-4">
                        <button class="btn btn-outline-primary me-2" @click="loadRecordingConfig">读取设置</button>
                        <button class="btn btn-success" @click="saveRecordingConfig">保存设置</button>
                    </div>
                </div>
                <div class="tips-row mb-0">
                    <span>系统只保留这一组映射。Unity 登录该 recording 账号时，会显示调用账号的最新大模型回复。</span>
                    <span v-if="configMessage" class="info-message">{{ configMessage }}</span>
                </div>
            </div>

            <div class="row g-3 filter-row">
                <div class="col-md-4">
                    <label class="form-label">查看密钥</label>
                    <input v-model="filters.accessKey" type="text" class="form-control" placeholder="输入 accessKey">
                </div>
                <div class="col-md-4">
                    <label class="form-label">用户 ID</label>
                    <input v-model="filters.username" type="text" class="form-control" placeholder="按用户 ID 筛选">
                </div>
                <div class="col-md-4">
                    <label class="form-label">设备 ID</label>
                    <input v-model="filters.deviceId" type="text" class="form-control" placeholder="按 deviceId 筛选">
                </div>
                <div class="col-md-4">
                    <label class="form-label">设备型号</label>
                    <input v-model="filters.deviceModel" type="text" class="form-control" placeholder="按设备型号筛选">
                </div>
                <div class="col-md-4">
                    <label class="form-label">会话 ID</label>
                    <input v-model="filters.sessionId" type="text" class="form-control" placeholder="按 sessionId 筛选">
                </div>
                <div class="col-md-4">
                    <label class="form-label">关键词</label>
                    <input v-model="filters.keyword" type="text" class="form-control" placeholder="搜索提问或回复">
                </div>
            </div>

            <div class="tips-row">
                <span>每页 20 条，共 {{ total }} 条，当前第 {{ page }} / {{ totalPages }} 页。</span>
                <span v-if="errorMessage" class="error-message">{{ errorMessage }}</span>
            </div>

            <div class="table-responsive">
                <table class="table table-striped table-hover align-middle chat-log-table">
                    <thead>
                    <tr>
                        <th class="id-column">ID</th>
                        <th class="user-column">用户 ID</th>
                        <th>设备 ID</th>
                        <th>设备型号</th>
                        <th>提问</th>
                        <th class="reply-column">回复</th>
                        <th>时间</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="!loading && logs.length === 0">
                        <td colspan="7" class="empty-cell">暂无数据</td>
                    </tr>
                    <tr v-for="log in logs" :key="log.id">
                        <td class="id-column">{{ log.id }}</td>
                        <td class="user-column">{{ log.username }}</td>
                        <td>{{ log.deviceId || '-' }}</td>
                        <td>{{ log.deviceModel || '-' }}</td>
                        <td class="message-cell">{{ log.userMessage }}</td>
                        <td class="message-cell reply-column">{{ log.assistantMessage || '-' }}</td>
                        <td>{{ log.createtime || '-' }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div class="pager-row">
                <button class="btn btn-outline-secondary" :disabled="page <= 1 || loading" @click="loadLogs(page - 1)">上一页</button>
                <button class="btn btn-outline-secondary" :disabled="page >= totalPages || loading" @click="loadLogs(page + 1)">下一页</button>
            </div>
        </div>
    </ContentField>
</template>

<script>
import { computed, reactive, ref } from 'vue'
import $ from 'jquery'
import ContentField from '@/components/ContentField.vue/'

const CHAT_LOG_KEY = "chat_log_access_key";
const API_BASE = "https://aiserver.arvreducation.org";
const PAGE_SIZE = 20;

export default {
    components: {
        ContentField
    },
    setup() {
        const logs = ref([]);
        const loading = ref(false);
        const errorMessage = ref("");
        const configMessage = ref("");
        const page = ref(1);
        const total = ref(0);
        const filters = reactive({
            accessKey: localStorage.getItem(CHAT_LOG_KEY) || "",
            username: "",
            deviceId: "",
            deviceModel: "",
            sessionId: "",
            keyword: "",
        });
        const recordingConfig = reactive({
            recordingUsername: "",
            targetUsername: "",
        });
        const totalPages = computed(() => Math.max(1, Math.ceil(total.value / PAGE_SIZE)));

        const validateAccessKey = () => {
            if (!filters.accessKey) {
                errorMessage.value = "请先输入查看密钥。";
                return false;
            }
            localStorage.setItem(CHAT_LOG_KEY, filters.accessKey);
            return true;
        };

        const loadLogs = (nextPage = 1) => {
            errorMessage.value = "";
            if (!validateAccessKey()) {
                logs.value = [];
                total.value = 0;
                return;
            }

            loading.value = true;
            $.ajax({
                url: `${API_BASE}/chatlog/list`,
                type: "get",
                data: {
                    accessKey: filters.accessKey,
                    username: filters.username,
                    deviceId: filters.deviceId,
                    deviceModel: filters.deviceModel,
                    sessionId: filters.sessionId,
                    keyword: filters.keyword,
                    page: nextPage,
                    size: PAGE_SIZE,
                },
                success(resp) {
                    logs.value = Array.isArray(resp) ? resp : (resp.records || []);
                    total.value = Number(resp.total || logs.value.length || 0);
                    page.value = Number(resp.page || nextPage);
                    if (logs.value.length === 0) {
                        errorMessage.value = "未查询到记录，或查看密钥不正确。";
                    }
                },
                error() {
                    logs.value = [];
                    total.value = 0;
                    errorMessage.value = "查询失败，请检查服务器接口是否可访问。";
                },
                complete() {
                    loading.value = false;
                }
            });
        };

        const loadRecordingConfig = () => {
            configMessage.value = "";
            if (!validateAccessKey()) return;
            $.ajax({
                url: `${API_BASE}/recording/config`,
                type: "get",
                data: { accessKey: filters.accessKey },
                success(resp) {
                    if (resp.error_message !== "success") {
                        configMessage.value = resp.error_message || "读取失败。";
                        return;
                    }
                    recordingConfig.recordingUsername = resp.recordingUsername || "";
                    recordingConfig.targetUsername = resp.targetUsername || "";
                    configMessage.value = "设置已读取。";
                },
                error() {
                    configMessage.value = "读取设置失败。";
                }
            });
        };

        const saveRecordingConfig = () => {
            configMessage.value = "";
            if (!validateAccessKey()) return;
            if (!recordingConfig.recordingUsername) {
                configMessage.value = "请填写唯一 recording 账号。";
                return;
            }
            if (!recordingConfig.targetUsername) {
                configMessage.value = "请填写唯一调用账号。";
                return;
            }
            $.ajax({
                url: `${API_BASE}/recording/config`,
                type: "post",
                contentType: "application/json",
                data: JSON.stringify({
                    accessKey: filters.accessKey,
                    recordingUsername: recordingConfig.recordingUsername,
                    targetUsername: recordingConfig.targetUsername,
                }),
                success(resp) {
                    configMessage.value = resp.error_message === "success" ? "设置已保存，系统只会保留这一组映射。" : (resp.error_message || "保存失败。");
                },
                error() {
                    configMessage.value = "保存设置失败。";
                }
            });
        };

        return {
            logs,
            loading,
            errorMessage,
            configMessage,
            filters,
            recordingConfig,
            page,
            total,
            totalPages,
            loadLogs,
            loadRecordingConfig,
            saveRecordingConfig,
        };
    }
}
</script>

<style scoped>
h3 {
    margin-bottom: 4px;
}

.chat-log-page {
    padding: 20px;
}

.header-row,
.tips-row,
.pager-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
}

.header-row {
    margin-bottom: 20px;
}

.sub-title,
.tips-row {
    color: #6c757d;
    font-size: 14px;
}

.recording-panel {
    padding: 16px;
    margin-bottom: 20px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
}

.filter-row,
.tips-row {
    margin-bottom: 16px;
}

.chat-log-table {
    table-layout: fixed;
    min-width: 1320px;
}

.id-column {
    width: 52px;
}

.user-column {
    width: 86px;
    max-width: 86px;
    word-break: break-word;
}

.message-cell {
    width: 280px;
    white-space: pre-wrap;
    word-break: break-word;
}

.reply-column {
    width: 560px;
}

.empty-cell {
    text-align: center;
    color: #6c757d;
}

.error-message,
.status-error {
    color: #dc3545;
}

.info-message,
.status-success {
    color: #198754;
}

.status-success,
.status-error {
    font-weight: 600;
}

.pager-row {
    justify-content: flex-end;
}
</style>
